import { useState } from "react";
import { useDispatch } from "react-redux";
import { FileText, Link as LinkIcon, Type, Trash2, CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { showAlert } from "../../../../store/alertSlice";
import { Button } from "../../../../components/tailwind/button";
import { Input } from "../../../../components/tailwind/input";
import { Textarea } from "../../../../components/tailwind/textarea";
import { Switch } from "../../../../components/ui/switch";
import { workflowTheme } from "../../utils/workflowBuilderTheme";
import { waBtn } from "../../utils/whatsappUiTokens";
import { cn } from "../../../../lib/utils";

export function KnowledgeBaseTab() {
  const dispatch = useDispatch();
  const [sources, setSources] = useState([]);
  const [urlInput, setUrlInput] = useState("");
  const [textInput, setTextInput] = useState("");
  
  const handleFileUpload = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    const newSource = {
      id: Date.now().toString(),
      type: "file",
      name: file.name,
      size: "Extracting...",
      updatedAt: "Just now",
      isActive: true,
      status: "loading"
    };
    
    setSources(prev => [newSource, ...prev]);
    
    // Mock processing
    setTimeout(() => {
      const isError = file.name.endsWith(".exe") || file.size > 5000000;
      setSources(prev => prev.map(s => {
        if (s.id === newSource.id) {
          if (isError) {
            return { ...s, status: "error", size: "Error: could not read file. Try a different format." };
          }
          return { ...s, status: "success", size: `${Math.floor(Math.random() * 50) + 1} pages extracted successfully` };
        }
        return s;
      }));
    }, 2000);
  };

  const handleUrlAdd = () => {
    if (!urlInput.trim()) return;
    
    const newSource = {
      id: Date.now().toString(),
      type: "url",
      name: urlInput,
      size: "Crawling website...",
      updatedAt: "Just now",
      isActive: true,
      status: "loading"
    };
    
    setSources(prev => [newSource, ...prev]);
    setUrlInput("");
    
    // Mock processing
    setTimeout(() => {
      setSources(prev => prev.map(s => {
        if (s.id === newSource.id) {
          return { ...s, status: "success", size: `${Math.floor(Math.random() * 20) + 1} pages found, content extracted.` };
        }
        return s;
      }));
    }, 3000);
  };

  const handleTextAdd = () => {
    if (!textInput.trim()) return;
    
    const newSource = {
      id: Date.now().toString(),
      type: "text",
      name: "Pasted Text " + new Date().toLocaleTimeString(),
      size: `${textInput.split(' ').length} words`,
      updatedAt: "Just now",
      isActive: true,
      status: "success",
      content: textInput
    };
    
    setSources(prev => [newSource, ...prev]);
    setTextInput("");
    dispatch(showAlert({ message: "Text added to Knowledge Base", type: "success" }));
  };

  const toggleSource = (id) => {
    setSources(prev => prev.map(s => s.id === id ? { ...s, isActive: !s.isActive } : s));
  };

  const deleteSource = (id) => {
    setSources(prev => prev.filter(s => s.id !== id));
  };

  const activeSourcesCount = sources.filter(s => s.isActive && s.status === "success").length;
  // Mock word count for the summary
  const totalWords = activeSourcesCount * 4500 + Math.floor(Math.random() * 1000);

  return (
    <div className={`flex-1 flex flex-col h-full ${workflowTheme.page} overflow-hidden`}>
      <div className="max-w-5xl mx-auto w-full p-8 overflow-y-auto flex-1">
        <div className="mb-8">
          <h2 className={`text-2xl font-bold ${workflowTheme.textPrimary} mb-2`}>Knowledge Base</h2>
          <p className={workflowTheme.textSecondary}>Add documents, websites, or raw text to give the AI context when generating or running workflows.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
          {/* Upload Section */}
          <div className={`${workflowTheme.surface} p-5 rounded-xl border ${workflowTheme.border} shadow-sm flex flex-col`}>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-blue-50 dark:bg-blue-950/50 rounded flex items-center justify-center text-blue-600 dark:text-blue-400">
                <FileText className="w-4 h-4" />
              </div>
              <h3 className={`font-semibold ${workflowTheme.textPrimary}`}>Upload File</h3>
            </div>
            <p className={`text-xs ${workflowTheme.textSecondary} mb-4 flex-1`}>Support for PDF, DOCX, TXT, and XLSX files up to 5MB.</p>
            <div className="relative">
              <input 
                type="file" 
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                accept=".pdf,.docx,.txt,.xlsx"
                onChange={handleFileUpload}
              />
              <Button variant="outline" className={`w-full border-dashed ${workflowTheme.input} hover:bg-gray-100 dark:hover:bg-slate-800`}>
                Choose File...
              </Button>
            </div>
          </div>

          {/* URL Section */}
          <div className={`${workflowTheme.surface} p-5 rounded-xl border ${workflowTheme.border} shadow-sm flex flex-col`}>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-green-50 dark:bg-green-950/50 rounded flex items-center justify-center text-green-600 dark:text-green-400">
                <LinkIcon className="w-4 h-4" />
              </div>
              <h3 className={`font-semibold ${workflowTheme.textPrimary}`}>Website URL</h3>
            </div>
            <p className={`text-xs ${workflowTheme.textSecondary} mb-4 flex-1`}>We'll crawl the top-level page to extract readable text.</p>
            <div className="flex gap-2">
              <Input 
                placeholder="https://example.com" 
                value={urlInput}
                onChange={e => setUrlInput(e.target.value)}
                className="text-sm"
              />
              <Button onClick={handleUrlAdd} disabled={!urlInput.trim()} className={waBtn.primary}>Add</Button>
            </div>
          </div>

          {/* Text Section */}
          <div className={`${workflowTheme.surface} p-5 rounded-xl border ${workflowTheme.border} shadow-sm flex flex-col`}>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-purple-50 dark:bg-purple-950/50 rounded flex items-center justify-center text-purple-600 dark:text-purple-400">
                <Type className="w-4 h-4" />
              </div>
              <h3 className={`font-semibold ${workflowTheme.textPrimary}`}>Raw Text</h3>
            </div>
            <p className={`text-xs ${workflowTheme.textSecondary} mb-4`}>Paste text directly. No processing needed.</p>
            <div className="space-y-2">
              <Textarea 
                placeholder="Paste your content here..." 
                className="h-14 resize-none text-sm"
                value={textInput}
                onChange={e => setTextInput(e.target.value)}
              />
              <Button onClick={handleTextAdd} disabled={!textInput.trim()} className={cn("w-full", waBtn.primary)} size="sm">Save Text</Button>
            </div>
          </div>
        </div>

        <div>
          <h3 className={`font-semibold ${workflowTheme.textPrimary} mb-4 flex items-center justify-between`}>
            Active Sources
            {sources.length > 0 && <span className={`text-xs font-normal ${workflowTheme.textSecondary} ${workflowTheme.surface} px-2 py-1 border ${workflowTheme.border} rounded-md shadow-sm`}>{activeSourcesCount} active sources · approx {totalWords.toLocaleString()} words available to AI</span>}
          </h3>
          
          {sources.length === 0 ? (
            <div className={`text-center py-12 ${workflowTheme.surface} rounded-xl border border-dashed border-gray-300 dark:border-slate-700`}>
              <p className={`${workflowTheme.textSecondary} text-sm`}>No knowledge sources added yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sources.map(source => (
                <div key={source.id} className={`${workflowTheme.surface} p-4 rounded-xl border flex items-center justify-between transition-all ${!source.isActive ? `opacity-60 ${workflowTheme.border}` : source.status === 'error' ? 'border-red-200 dark:border-red-900 bg-red-50/30 dark:bg-red-950/30' : `${workflowTheme.border} shadow-sm hover:border-[#0F6E56]/30 dark:hover:border-emerald-700/50`}`}>
                  <div className="flex items-center gap-4 flex-1">
                    <div className={`w-10 h-10 rounded-lg bg-gray-50 dark:bg-slate-800 flex items-center justify-center shrink-0 border ${workflowTheme.borderSubtle}`}>
                      {source.type === 'file' && <FileText className="w-5 h-5 text-blue-600" />}
                      {source.type === 'url' && <LinkIcon className="w-5 h-5 text-green-600" />}
                      {source.type === 'text' && <Type className="w-5 h-5 text-purple-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className={`font-semibold ${workflowTheme.textPrimary} text-sm truncate`}>{source.name}</h4>
                        {source.status === 'loading' && <Loader2 className="w-3.5 h-3.5 text-blue-500 animate-spin" />}
                        {source.status === 'success' && <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />}
                        {source.status === 'error' && <AlertCircle className="w-3.5 h-3.5 text-red-500" />}
                      </div>
                      <div className="flex items-center gap-3 text-xs mt-0.5">
                        <span className={source.status === 'error' ? 'text-red-500 dark:text-red-400 font-medium' : source.status === 'loading' ? 'text-blue-500 dark:text-blue-400 font-medium animate-pulse' : workflowTheme.textSecondary}>
                          {source.size}
                        </span>
                        <span className="text-gray-300 dark:text-gray-600">•</span>
                        <span className={workflowTheme.textMuted}>Updated {source.updatedAt}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className={`flex items-center gap-6 ml-4 pl-4 border-l ${workflowTheme.borderSubtle}`}>
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={source.isActive} 
                        onCheckedChange={() => toggleSource(source.id)} 
                        disabled={source.status === 'loading' || source.status === 'error'}
                        className="data-[state=checked]:bg-[#0F6E56]"
                      />
                      <span className={`text-xs font-medium ${workflowTheme.textSecondary} w-12`}>{source.isActive ? 'Active' : 'Ignored'}</span>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => deleteSource(source.id)} className="text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 h-8 w-8">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}